/**
 * The benefits intake — six questions, asked by a navigator sitting with her.
 *
 * It reuses the screener renderer rather than forking it: the instrument lives
 * in the same screening_* tables, and a second questionnaire component would be
 * a second place for autosave, pagination and accessibility to drift. What is
 * supplied here is the API adapter (this instrument is served from /api/benefits,
 * not from the social-needs router) and the words.
 *
 * THE DERIVED STRIP IS READ BACK FROM THE SERVER, NOT COMPUTED HERE.
 * "She is adjunctively eligible for WIC" is a rule, and a rule implemented
 * twice is a rule that will disagree with itself. The strip debounces a save
 * and re-reads what the server derived, so the number on screen and the number
 * the impact engine uses are the same number.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Info, ShieldCheck, AlertTriangle, Loader2 } from 'lucide-react';
import { ScreenerQuestionnaire, type ScreenerApi } from '../social-needs-screener/ScreenerQuestionnaire';
import type { InstrumentDetail, ItemResponse } from '../social-needs-screener/types';
import { benefitsApi, type DerivedIntake } from './types';

const DERIVE_DEBOUNCE_MS = 1200;

/** The benefits router behind the renderer's three-call interface. */
function makeApi(onDerived: (d: DerivedIntake) => void): ScreenerApi {
  return {
    async getInstrument(): Promise<InstrumentDetail> {
      const d = await benefitsApi.getInstrument();
      return {
        instrument: d.instrument,
        // This instrument has one domain and the renderer does not use it.
        domains: [],
        items: d.items.map((it) => ({
          ...it,
          domain_code: it.domain_code ?? '',
          domain_name: it.domain_name ?? '',
        })),
      } as InstrumentDetail;
    },
    async saveResponses(administrationId: string, body: { responses: ItemResponse[] }) {
      await benefitsApi.saveResponses(
        Number(administrationId),
        body.responses.map((r) => ({ item_number: r.item_number, raw_response: r.raw_response })),
      );
      const read = await benefitsApi.readIntake(Number(administrationId));
      onDerived(read.derived);
      return { safety_triggered: false };
    },
    async submit(administrationId: string) {
      await benefitsApi.submitIntake(Number(administrationId));
      return { safety_triggered: false };
    },
  };
}

const DerivedStrip: React.FC<{ derived: DerivedIntake | null; pending: boolean }> = ({
  derived,
  pending,
}) => {
  if (!derived) return null;
  const { benefits_received: benefits, adjunctively_eligible, has_ssi, uncertain } = derived;
  if (benefits.length === 0 && uncertain.length === 0) return null;

  return (
    <div className="rounded-lg border border-border bg-muted/50 p-3">
      <div className="flex items-center gap-2">
        <Info className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm font-medium text-foreground">So far</p>
        {pending && (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden="true" />
        )}
      </div>

      {benefits.length > 0 && (
        <p className="mt-2 text-sm text-foreground">
          Receiving: <span className="font-medium">{benefits.join(', ')}</span>
        </p>
      )}

      {adjunctively_eligible && benefits.includes('WIC') && (
        <p className="mt-2 flex items-start gap-2 text-sm text-emerald-800 dark:text-emerald-200">
          <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>
            Adjunctively eligible for WIC through SNAP or Medicaid, so WIC does not income-test
            her at all. If she later loses SNAP, that protection goes with it — a SNAP loss is a
            WIC risk one step removed.
          </span>
        </p>
      )}

      {has_ssi && (
        <p className="mt-2 flex items-start gap-2 text-sm text-red-800 dark:text-red-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>
            Someone in this household receives SSI. SSI is the one benefit with no available
            exemption — do not continue without a counsellor.
          </span>
        </p>
      )}

      {uncertain.length > 0 && (
        <p className="mt-2 flex items-start gap-2 text-sm text-violet-800 dark:text-violet-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>
            Unsure about: {uncertain.join(', ')}. Carried as open rather than assumed either way.
          </span>
        </p>
      )}
    </div>
  );
};

interface Props {
  memberId: number;
  onSubmitted?: (administrationId: number) => void;
}

export const BenefitsIntakeQuestionnaire: React.FC<Props> = ({ memberId, onSubmitted }) => {
  const [administrationId, setAdministrationId] = useState<number | null>(null);
  const [resumed, setResumed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [derived, setDerived] = useState<DerivedIntake | null>(null);
  const [derivePending, setDerivePending] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    benefitsApi
      .startIntake(memberId)
      .then((r) => {
        if (cancelled) return;
        setAdministrationId(r.administration_id);
        setResumed(r.resumed);
        return benefitsApi.readIntake(r.administration_id).then((read) => {
          if (!cancelled) setDerived(read.derived);
        });
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not open the intake.');
      });
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  const api = useRef<ScreenerApi | null>(null);
  if (!api.current) {
    api.current = makeApi((d) => {
      setDerived(d);
      setDerivePending(false);
    });
  }

  /**
   * Every answer change schedules a save-and-reread. The renderer autosaves on
   * its own 20-second tick too; this is the faster loop that keeps the strip
   * honest while she is still answering.
   */
  const onAnswersChange = useCallback(() => {
    if (administrationId == null) return;
    if (timer.current) window.clearTimeout(timer.current);
    setDerivePending(true);
    timer.current = window.setTimeout(() => {
      benefitsApi
        .readIntake(administrationId)
        .then((r) => setDerived(r.derived))
        .catch(() => undefined)
        .finally(() => setDerivePending(false));
    }, DERIVE_DEBOUNCE_MS);
  }, [administrationId]);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        {error}
      </div>
    );
  }

  if (administrationId == null) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
        Opening the intake…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      {resumed && (
        <p className="rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
          Continuing the intake that was already open for this member.
        </p>
      )}

      <DerivedStrip derived={derived} pending={derivePending} />

      <ScreenerQuestionnaire
        administrationId={String(administrationId)}
        instrumentCode="BENEFITS_INTAKE"
        api={api.current}
        onAnswersChange={onAnswersChange}
        copy={{
          intro:
            'These questions tell us what a payment would do to the benefits this household already receives. Guessing wrong costs her more than asking, so tick anything you are unsure about.',
          submitLabel: 'Finish intake',
          doneTitle: 'Intake saved.',
          doneBody:
            'The benefit impact is on the Benefits tab. Nothing here is a determination by an agency, and nothing goes to the member without a counsellor.',
        }}
        onSubmitted={() => onSubmitted?.(administrationId)}
      />
    </div>
  );
};

export default BenefitsIntakeQuestionnaire;
